/**
 * The Analytics & Insights panel's **per-hierarchy breakdown** — client directive,
 * 9 Sep 2026, item 3.
 *
 * A donut and a bar chart describing the rung *below* whatever the reader has filtered to,
 * mounted once inside `IndicatorAnalytics` and therefore serving both families the directive
 * names: the 2025 MELS survey estimates behind a baseline chip, and the contractual and
 * thematic roll-up indicators behind the Indicators icon. The shaping — which rung, which
 * rows, and whether a ring would be honest — is `breakdown.ts`; this file is the fetching and
 * the drawing.
 *
 * ## What it replaces
 *
 * The panel's old "By country · {year}" block, which rendered **only** at the unfiltered
 * programme extent (`country === 'ALL' && !scope`). That is exactly backwards from what a
 * filter is for: the moment a reader narrowed to Burkina Faso, the one chart that could have
 * said *where inside Burkina Faso* disappeared. The programme case is still the same chart —
 * countries are the rung below the programme — it is now the first row of a table rather than
 * the only row that exists.
 *
 * ## What it costs
 *
 * Two requests, both composed from endpoints the explorer already serves rather than a new
 * one: `/indicators/{id}/values?level=&parent=&country=` (which already carries `sample_n`
 * per row) and `/hierarchy/options?type=&level=&parent=` — the cascade's cheap name list, not
 * the polygons. Under a country filter the §F5.8 cascade is holding that same `options` entry,
 * and at the programme extent the values entry is the one the choropleth itself fetched, so
 * the usual cost of this block is one request and often none.
 *
 * ## Two-way link
 *
 * The bars re-scope with the cascade because their query key contains the scope, the country
 * and the year. A **map click** (`selectedUnit`) does not re-scope them — that would move the
 * headline above them, and the panel's rule is that a filter is a standing instruction where
 * a click is a glance — but it does *mark* its row, so clicking a country on the map picks it
 * out of the four bars. That is the §F5.3 → §F5.2 half of the link, at chart resolution.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { BarsH } from '../../components/charts/BarsH';
import { Donut } from '../../components/charts/Donut';
import {
  fetchConfig,
  fetchUnitOptions,
  fetchValues,
  modeFor,
  queryKeys,
  type Indicator,
} from '../../lib/api';
import { fmtNum } from '../../lib/format';
import { countryColor } from '../../lib/palette';
import { selectYear, useAppStore } from '../../lib/state';
import { autoRamp, FALLBACK_RAMP_STOPS, sampleRamp } from '../../lib/symbology';
import {
  breakdownPlan,
  breakdownRows,
  donutShape,
  MAX_BREAKDOWN_ROWS,
  type BreakdownRow,
} from './breakdown';

/** The unspent half of a share ring — the mockup's inert panel grey, as the gauge uses. */
const REST_FILL = '#EEF1EC';

/** How far a bar fades when the reader has clicked some *other* unit on the map. */
const CONTEXT_OPACITY = 0.34;

/** `#RRGGBB` → the same hue at `CONTEXT_OPACITY`; anything else passes through. */
function dim(color: string): string {
  const hex = /^#([0-9a-f]{6})$/i.exec(color.trim())?.[1];
  if (!hex) return color;
  const n = parseInt(hex, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${CONTEXT_OPACITY})`;
}

export interface HierarchyBreakdownProps {
  /** The catalogue row the panel is about. */
  ind: Indicator;
  /** The figure the panel is already printing, for the share ring's centre. */
  value: number | null;
  /** The panel's domain colour — the ramp the bars are drawn from starts here. */
  color: string;
  /** Chart width; the sidebar section is a little under 292 px. */
  width: number;
  /** False while the sidebar is collapsed — the panel stays mounted, so this gates the reads. */
  enabled?: boolean;
  /**
   * Whether to draw the bars — default `true`.
   *
   * The caller passes `false` at the unfiltered programme extent, where §F5.9's own
   * "By country · {year}" `BarsV` is already on screen above with §G8's fixed palette. Two
   * charts of the same four numbers, one under the other, is not a richer panel; the ring is
   * still drawn there because it says something the bars do not (the composition, and its
   * total). Every rung below the programme draws both.
   */
  showBars?: boolean;
}

export function HierarchyBreakdown({
  ind,
  value,
  color,
  width,
  enabled = true,
  showBars = true,
}: HierarchyBreakdownProps) {
  const { t } = useTranslation();
  const scope = useAppStore((s) => s.scope);
  const country = useAppStore((s) => s.country);
  const selectedUnit = useAppStore((s) => s.selectedUnit);
  const year = useAppStore(selectYear);
  const cumulative = useAppStore((s) => s.cumulative);
  const mode = modeFor(cumulative, ind);

  const { data: config } = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });
  const plan = useMemo(() => breakdownPlan(ind, scope, country), [ind, scope, country]);
  const on = enabled && plan !== null;

  const { data: values } = useQuery({
    queryKey: queryKeys.values(ind.id, plan?.level ?? 0, year, country, mode, plan?.parent ?? null),
    queryFn: () =>
      fetchValues(ind.id, plan?.level ?? 0, year, country, mode, plan?.parent ?? null),
    enabled: on,
  });
  const { data: options } = useQuery({
    queryKey: queryKeys.unitOptions(
      plan?.unitType ?? 'admin',
      typeof plan?.level === 'number' ? plan.level : null,
      country,
      plan?.parent ?? null,
    ),
    queryFn: () =>
      fetchUnitOptions(
        plan?.unitType ?? 'admin',
        typeof plan?.level === 'number' ? plan.level : null,
        country,
        plan?.parent ?? null,
      ),
    enabled: on,
  });

  /**
   * The rows, and the palette they are drawn in.
   *
   * Countries keep §G8's fixed four colours — the palette every other country chart in the
   * portal uses, so a reader recognises Mali's blue without reading the label. Every other
   * rung is sampled from **the ramp the choropleth is painting with** (`autoRamp` over the
   * indicator's domain), darkest at the top: the bar for a watershed is then the same colour
   * family as the polygon beside it, which is the whole point of putting them on one screen.
   */
  const rows = useMemo(() => breakdownRows(values?.values, options), [values, options]);
  const shown = rows.slice(0, MAX_BREAKDOWN_ROWS);
  const rowColors = useMemo(() => {
    if (plan?.level === 0) return shown.map((r) => countryColor(r.iso ?? ''));
    const stops =
      config?.ramps[autoRamp(ind.id, ind.domain)] ??
      config?.ramps.green ??
      FALLBACK_RAMP_STOPS;
    // Sampled over the rows actually drawn, darkest first — `sampleRamp` keeps both ends, so
    // a two-row breakdown is the ramp's extremes rather than two neighbouring shades.
    const ramp = sampleRamp(stops, Math.max(2, shown.length));
    return shown.map((_, i) => ramp[ramp.length - 1 - i] ?? color);
  }, [shown, plan, config, ind, color]);

  const donut = useMemo(() => donutShape(ind, value, rows), [ind, value, rows]);
  const total = donut?.kind === 'composition' ? donut.total : null;

  if (!plan || !on) return null;

  const marked = shown.some((r) => r.unitId === selectedUnit) ? selectedUnit : null;
  const barColors = shown.map((r, i) => {
    const base = rowColors[i] ?? color;
    return !marked || r.unitId === marked ? base : dim(base);
  });
  const heading = t(plan.labelKey);

  return (
    <div className="bd-block" data-breakdown={plan.level} data-bdrung={plan.rung}>
      {/*
        The ring, where one would be true — see `donutShape`. Two shapes, because the two
        indicator families compose differently and neither may borrow the other's chart.
      */}
      {donut?.kind === 'share' ? (
        <>
          <h5 className="ipo-h5" data-bddonut="share">
            {t('breakdown.shareTitle')}
          </h5>
          <div className="bd-donut">
            <Donut
              items={[
                { label: t('breakdown.shareSlice'), value: donut.value, color },
                { label: t('breakdown.shareRest'), value: 100 - donut.value, color: REST_FILL },
              ]}
              size={92}
              centerLabel={`${fmtNum(donut.value, ind.fmt)}%`}
              // No centre sub-label: the heading above already says what the ring is a share
              // of, and a second caption inside a 92 px ring is a line of 8.6 px text laid
              // over the arc it is describing.
              fmt={ind.fmt}
              ariaLabel={t('breakdown.shareAria', { indicator: ind.name })}
            />
          </div>
        </>
      ) : donut?.kind === 'composition' && shown.length > 1 ? (
        <>
          <h5 className="ipo-h5" data-bddonut="composition">
            {t('breakdown.compositionTitle', { rung: heading })}
          </h5>
          <div className="bd-donut">
            <Donut
              items={shown.map((row, i) => ({
                label: row.name,
                value: row.value,
                color: rowColors[i] ?? color,
              }))}
              size={92}
              centerLabel={total === null ? '' : fmtNum(total, 0)}
              centerSub={ind.unit ?? ''}
              fmt={ind.fmt}
              ariaLabel={t('breakdown.compositionAria', { indicator: ind.name })}
            />
          </div>
        </>
      ) : null}

      {/*
        The bars. Dropped rather than drawn hollow when the rung below has no reported
        unit — for a survey estimate that is the ordinary, permanent state at a level it was
        never measured at, and an axis with no bar on it reads as a chart that failed.
      */}
      {showBars && shown.length ? (
        <>
          <h5 className="ipo-h5" data-bdbars={plan.level}>
            {heading}
          </h5>
          <BarsH
            labels={shown.map((r) => r.name)}
            series={[
              {
                key: 'breakdown',
                label: ind.unit ?? heading,
                color,
                values: shown.map((r) => r.value),
                rowColors: barColors,
              },
            ]}
            w={width}
            rowH={18}
            labelGutter={72}
            labelSize={10}
            unit={ind.unit ?? undefined}
            fmt={ind.fmt}
            ariaLabel={t('breakdown.barsAria', { indicator: ind.name, rung: heading })}
          />
          {rows.length > shown.length ? (
            <div className="tbl-note" data-bdmore="">
              {t('breakdown.more', { n: rows.length - shown.length })}
            </div>
          ) : null}
          {/* The survey's own qualifier, where §D3 sent one: how many households each bar is
              an estimate over. It belongs under the chart for the same reason the headline's
              n belongs on the scope line — it says what the numbers are *of*. */}
          {shown.some((r) => r.sampleN !== null) ? (
            <div className="tbl-note" data-bdsample="">
              {t('breakdown.sample', { n: fmtNum(sampleTotal(shown), 0) })}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/** The sample the drawn bars rest on, summed — each unit was surveyed separately. */
function sampleTotal(rows: readonly BreakdownRow[]): number {
  return rows.reduce((sum, row) => sum + (row.sampleN ?? 0), 0);
}

export default HierarchyBreakdown;
